import { GET_ORGANIZATIONS } from 'api'
import { getOrganizations } from 'api/get/superAdminDashboard/superAdminDashboard'
import {
	Button,
	Input,
	OrganizationsTable,
	SuperAdminLayout,
	Title
} from 'components'
import { BuildAppsContext } from 'context'
import { useSearchDebounce } from 'hooks'
import {
	GetOrganizationsResponse,
	Icons,
	OrganizationType,
	TypeSortBy
} from 'interfaces'
import React, { useContext, useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useQuery } from 'react-query'

export const SuperAdminDashboard = (): JSX.Element => {
	const [tablePage, setTablePage] = useState(1)
	const [pageLimit, setPageLimit] = useState(10)
	const [pageSearch, setPageSearch] = useState(1)
	const [sort, setSort] = useState<TypeSortBy[]>([
		{
			id: 'last_active',
			desc: true
		}
	])
	const { isChangeData } = useContext(BuildAppsContext)

	// for client request, inactive temporary
	// const { data: lastOrganizations, isLoading: isLastOrganizationsLoading } =
	// 	useQuery(
	// 		[
	// 			GET_ORGANIZATIONS,
	// 			{ orderBy: 'desc', columnOrder: 'last_active', page: 1 }
	// 		],
	// 		() =>
	// 			getOrganizations({
	// 				orderBy: 'desc',
	// 				columnOrder: 'last_active',
	// 				page: 1
	// 			}),
	// 		{ staleTime: Infinity }
	// 	)

	const { register, control, resetField } = useForm<{ search: string }>({
		mode: 'onChange'
	})

	const watchSearch = useWatch({
		control,
		name: 'search'
	})

	const { inputSearch, handleChangeDebounce, resetInput } =
		useSearchDebounce(watchSearch)
	const isSearching = inputSearch.length > 1

	const columnNane = sort?.[0]?.id
	const typeOrderBy = sort?.[0]?.desc === false ? 'asc' : 'desc'
	const paramsOrganization = {
		columnOrder: columnNane,
		orderBy: columnNane ? typeOrderBy : '',
		page: isSearching ? pageSearch : tablePage,
		search: inputSearch.trim(),
		size: pageLimit
	}
	const {
		data: organizations,
		isLoading: isLoadingOrganizations,
		isRefetching,
		refetch
	} = useQuery(
		[GET_ORGANIZATIONS, isChangeData, paramsOrganization, sort, pageLimit],
		() => getOrganizations(paramsOrganization),
		{ refetchOnWindowFocus: false }
	)

	useEffect(() => {
		if (!isSearching) {
			setPageSearch(1)
		}
	}, [isSearching])

	const formatOrganzations = (
		data?: GetOrganizationsResponse | null
	): OrganizationType[] =>
		data?.items?.map(org => ({
			address: org.address,
			build_app: `${org.build_app_id}-${org.build_app}`,
			contents: org.contents,
			id: org.id,
			email: org.admin_email || org.email || '',
			lastContent: org.last_content,
			lastActive: org.last_active,
			logo: org.icon_image_url,
			name: org.name,
			phoneNumber: org.phone_number,
			users: org.users,
			shareUrl: org.share_url,
			slug: org.slug,
			branchUrlMigration: org.branch_url_migration,
			branchUrlSso: org.branch_url_sso,
			organization_type: org?.organization_type ?? null,
			is_visible: org?.is_visible as boolean,
			active_import_csv: org?.active_import_csv as boolean,
			require_otp: org?.require_otp as boolean,
			active_resource: org?.active_resource as boolean,
			active_calendar_view: org?.active_calendar_view as boolean | null,
			active_my_network: org?.active_my_network as boolean
		})) || []

	return (
		<SuperAdminLayout>
			<div className="3xl:mx-auto 3xl:my-auto 3xl:w-3/4 mx-auto">
				<div className="flex flex-row justify-between h-full">
					<Title
						title="Organizations"
						subtitle="Manage Your Organizations"
						icon={Icons.dashboard}
					/>
					<Button
						linkTo="/superAdmin/organization/create"
						label="New Organization"
						color="Primary"
						className="py-2"
					/>
				</div>

				{/* for client request, inactive temporary
						
						{formatOrganzations(lastOrganizations).length === 0 ? (
							<div className="flex justify-center">
								<Typography
									fontLeading="12/15"
									title="You don't have any organizations."
									type={TypographyTypes.span}
									className="my-28 !text-gray-1 !font-normal"
								/>
							</div>
						) : (
							<LastOrganizationsList
								organizations={formatOrganzations(lastOrganizations)}
								styles="mt-6"
							/>
						)} */}

				<div className="flex items-center justify-between py-3 gap-8 w-full">
					<Input
						rightClick={() => {
							resetInput()
							resetField('search')
						}}
						name="search"
						onChange={handleChangeDebounce}
						search
						register={register}
						className="w-full"
						placeholder="Search an organization"
						rightIcon={Icons.cancel}
						inputClassName="!p-[unset] !py-1 !px-2 !rounded-[16px]"
						inputFocus
						borderFull={false}
						onChangeCustom={() => {
							if (tablePage !== 1) {
								setPageSearch(1)
							}
						}}
					/>
				</div>

				<div className="overflow-x-auto">
					<OrganizationsTable
						key={tablePage}
						noDataMessage={"You don't have any organizations."}
						organizations={formatOrganzations(organizations)}
						isFetching={isLoadingOrganizations || isRefetching}
						showPagination
						onPageChange={isSearching ? setPageSearch : setTablePage}
						page={isSearching ? pageSearch : tablePage}
						total={organizations?.total}
						pages={organizations?.size}
						itemsShowing={pageLimit}
						onShowingItemsChange={setPageLimit}
						refetchOrganizations={refetch}
						setSort={setSort}
						sort={sort}
					/>
				</div>
			</div>
		</SuperAdminLayout>
	)
}
